/* eslint-disable react-refresh/only-export-components -- this module exports a UI hook + 2 components */
import { AlertTriangleIcon, MicIcon, MicOffIcon, SquareIcon } from 'lucide-react';
import { useCallback, useState, type ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import { formatDuration } from '@/lib/format-duration';
import { cn } from '@/lib/utils';

import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useChatActions } from '../hooks/useChatActions';
import type { ErrorKind, RecorderState } from '../state/types';
import { MicPermissionDialog } from './MicPermissionDialog';

interface AudioRecorderUI {
  state: RecorderState;
  durationMs: number;
  errorKind: ErrorKind | null;
  helpOpen: boolean;
  isPending: boolean;
  isRecording: boolean;
  onToggle: () => void;
  dismissError: () => void;
  openHelp: () => void;
  closeHelp: () => void;
}

export function useAudioRecorderUI(): AudioRecorderUI {
  const { sendAudio } = useChatActions();
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const { state, durationMs, start, stop, reset } = useAudioRecorder({
    onComplete: ({ blob, mimeType, durationMs: ms }) => sendAudio(blob, mimeType, ms),
    onError: (kind) => {
      setErrorKind(kind);
    },
  });

  const isRecording = state === 'recording';
  const isPending = state === 'requesting' || state === 'uploading';

  const onToggle = useCallback(() => {
    if (isRecording) {
      stop();
      return;
    }
    setErrorKind(null);
    void start();
  }, [isRecording, start, stop]);

  const dismissError = useCallback(() => {
    setErrorKind(null);
    reset();
  }, [reset]);

  const openHelp = useCallback(() => {
    setHelpOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setHelpOpen(false);
  }, []);

  return {
    state,
    durationMs,
    errorKind,
    helpOpen,
    isPending,
    isRecording,
    onToggle,
    dismissError,
    openHelp,
    closeHelp,
  };
}

export function AudioRecorderStatusStrip({ ui }: { ui: AudioRecorderUI }): ReactElement | null {
  if (ui.isRecording) {
    return <RecordingHUD durationMs={ui.durationMs} />;
  }
  if (ui.errorKind !== null) {
    return <ErrorBanner kind={ui.errorKind} onDismiss={ui.dismissError} onOpenHelp={ui.openHelp} />;
  }
  return null;
}

export function AudioRecorderButton({
  ui,
  disabled = false,
}: {
  ui: AudioRecorderUI;
  disabled?: boolean;
}): ReactElement {
  return (
    <>
      <Button
        type="button"
        size="icon"
        variant={ui.isRecording ? 'destructive' : 'ghost'}
        aria-label={ui.isRecording ? 'Parar gravação' : 'Gravar áudio'}
        aria-pressed={ui.isRecording}
        disabled={disabled || ui.isPending}
        onClick={ui.onToggle}
        className={cn(
          'size-9 shrink-0 rounded-full',
          !ui.isRecording && 'text-muted-foreground hover:text-foreground',
          ui.isRecording && 'motion-safe:animate-[chat-pulse_1.2s_ease-in-out_infinite]',
        )}
      >
        {ui.isRecording ? <SquareIcon aria-hidden /> : <MicIcon aria-hidden />}
      </Button>
      <MicPermissionDialog
        open={ui.helpOpen}
        onOpenChange={(o) => (o ? ui.openHelp() : ui.closeHelp())}
      />
    </>
  );
}

function RecordingHUD({ durationMs }: { durationMs: number }): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="border-destructive/40 bg-destructive/10 text-destructive motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-200 flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs"
    >
      <span
        aria-hidden
        className="bg-destructive inline-block size-2 shrink-0 rounded-full motion-safe:animate-[chat-pulse_1.2s_ease-in-out_infinite]"
      />
      <span className="font-medium">
        Gravando · <span className="tabular-nums">{formatDuration(durationMs)}</span>
      </span>
      <span className="text-destructive/80 ml-auto hidden sm:inline">
        Toque no quadrado para parar
      </span>
    </div>
  );
}

function ErrorBanner({
  kind,
  onDismiss,
  onOpenHelp,
}: {
  kind: ErrorKind;
  onDismiss: () => void;
  onOpenHelp: () => void;
}): ReactElement {
  const isPermission = kind === 'NotAllowed';
  const title = isPermission
    ? 'Microfone bloqueado pelas configurações do navegador'
    : kind === 'NotFound'
      ? 'Nenhum microfone encontrado'
      : kind === 'NotSupported'
        ? 'Seu navegador não suporta gravação de áudio'
        : 'Não foi possível gravar o áudio';

  const body = isPermission
    ? 'Para usar a gravação de voz, você precisa liberar o acesso ao microfone.'
    : kind === 'NotFound'
      ? 'Conecte um microfone e tente novamente.'
      : kind === 'NotSupported'
        ? 'Tente usar um navegador atualizado como Chrome, Firefox ou Safari.'
        : 'Tente novamente em instantes.';

  return (
    <div
      role="alert"
      className="border-warning-border bg-warning-bg text-warning-fg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-200 flex gap-3 rounded-2xl border p-3 text-xs"
    >
      {kind === 'NotSupported' || kind === 'NotFound' ? (
        <MicOffIcon className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
      ) : (
        <AlertTriangleIcon className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
      )}
      <div className="flex-1">
        <p className="font-semibold leading-snug">{title}</p>
        <p className="mt-0.5 opacity-90">{body}</p>
        <div className="mt-2 flex gap-2">
          {isPermission && (
            <Button size="sm" variant="default" onClick={onOpenHelp}>
              Como liberar o acesso
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onDismiss}>
            Dispensar
          </Button>
        </div>
      </div>
    </div>
  );
}
